import React from 'react';
import {LinkedAccount} from '../../context/ApplicationContext';
import {Button, IconAdd, InPageAlert, SectionPage, TextLink} from '@snsw-gel/react';
import CannotLinkPersonalOrBusinessAlert from './CannotLinkPersonalOrBusinessAlert';
import {Bold} from '../../components/Bold';
import {displayableTollProvider} from '../../utils/helpers';
import config from '../../config';

interface SoleTraderProps {
    readonly weeklyCap: string;
    readonly mixedAccount?: LinkedAccount;
    readonly onMixedUnlink: () => void;
    readonly onMixedLink: () => void;

    readonly businessAccount?: LinkedAccount;
    readonly onBusinessUnlink: () => void;
    readonly onBusinessLink: () => void;
    readonly hasPersonal: boolean;
}

/**
 * The sole trader section of the MTA page.
 * Includes Mixed and Business sections.
 */
// TODO remove this after Tr2 decom
const SoleTrader: React.FC<SoleTraderProps> = (
    {
        weeklyCap,
        mixedAccount,
        onMixedUnlink,
        onMixedLink,
        businessAccount,
        onBusinessUnlink,
        onBusinessLink,
        hasPersonal,
    }: SoleTraderProps,
) => {
    const hasMixed = mixedAccount !== undefined;
    const hasBusiness = businessAccount !== undefined;

    return <>
        <h2>For sole traders</h2>
        <SectionPage>
            <p>Sole traders may be eligible to claim a business rebate under the 40% rebate scheme.</p>

            <p>To be eligible, you must:</p>
            <ul>
                <li>have an active ABN during the claim period, and</li>
                <li>operate within NSW.</li>
            </ul>

            <p>
                Find out more about{' '}
                <TextLink href={config.links.soleTraderEligibilityLink} id='eligibilityGuidelinesLink'
                          target='_blank'
                          rel='noreferrer noopener'>
                    eligibility guidelines
                </TextLink>
            </p>

            <p>If you're an eligible sole trader, you can link:</p>
            <ul>
                <li>One personal toll account for both personal and business use. You'll have a 'mixed' account.</li>
                <li>Separate personal and business toll accounts. You'll have 2 different toll accounts.</li>
                <li>One business toll account for business use only. You'll have one business account.</li>
            </ul>
        </SectionPage>

        <SectionPage>
            <h3>Business and personal (mixed personal toll account)</h3>
            {(hasBusiness || hasPersonal) &&
                <InPageAlert variant='info' title={'A business or personal toll account is already linked'}>
                    <p>
                        To link this type of 'mixed' personal toll account you need to remove all other linked toll accounts.
                        You can only link one personal or one business toll account at a time.
                    </p>
                </InPageAlert>
            }

            {hasMixed && displayTollAccountInfo(mixedAccount)}

            {hasMixed ?
                <Button variant='secondary' onClick={onMixedUnlink} id='button-unlink-mixed'>
                    Remove mixed toll account
                </Button>
                : <Button variant='secondary' iconStart={<IconAdd/>} onClick={onMixedLink} id='button-link-mixed'>
                    Link mixed toll account
                </Button>
            }
        </SectionPage>

        <SectionPage>
            <h3>Business toll account</h3>
            <p>
                Business customers can only claim from the 40% rebate scheme.
                This is where an ABN has been provided to E-Toll or Linkt.
            </p>

            {hasMixed && CannotLinkPersonalOrBusinessAlert}

            {hasBusiness && displayTollAccountInfo(businessAccount)}

            {hasBusiness ?
                <Button variant='secondary' onClick={onBusinessUnlink} id='button-unlink-business'>
                    Remove business toll account
                </Button>
                : <Button variant='secondary' iconStart={<IconAdd/>} onClick={onBusinessLink} id='button-link-business'>
                    Link business toll account
                </Button>
            }
        </SectionPage>
    </>;
};

export default SoleTrader;

/**
 * Display toll account info for business/mixed only.
 * Assumes tollAccount.businessDetails exists.
 */
function displayTollAccountInfo(tollAccount: LinkedAccount): React.JSX.Element {
    return <>
        <p>
            Toll provider: <Bold>{displayableTollProvider(tollAccount.tollProvider)}</Bold>
        </p>
        <p>
            Toll account number: <Bold>{tollAccount.tollAccountNumber}</Bold>
        </p>
        <p>
            Business name: <Bold>{tollAccount.businessDetails?.businessName}</Bold>
        </p>
        <p>
            ABN: <Bold>{tollAccount.businessDetails?.abn}</Bold>
        </p>
    </>;
}
